import { useCallback, useEffect, useMemo, useState } from "react";
import { deleteRow, insertRow, listRows, updateRow } from "@/lib/adminApi";
import {
  TESTIMONIAL_STATUS,
  metaFor,
  optionsFrom,
  type Testimonial,
} from "@/lib/adminContent";
import {
  DataTable,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  TextField,
  btnGhost,
  btnPrimary,
  fieldInput,
  textInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { useAdminToast } from "@/hooks/useAdminToast";

const EMPTY_TESTIMONIAL = {
  name: "",
  role: "",
  location: "",
  quote: "",
  experience: "",
  rating: 5,
  featured: true,
  sort_order: 0,
  status: "published",
};

function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-accent-500">
      {[1, 2, 3, 4, 5].map((star) => (
        <i key={star} className={star <= rating ? "ri-star-fill text-sm" : "ri-star-line text-sm text-foreground-300"}></i>
      ))}
    </span>
  );
}

export default function AdminTestimonials() {
  const [rows, setRows] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_TESTIMONIAL });
  const [saving, setSaving] = useState(false);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<Testimonial>("testimonials", "sort_order"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load testimonials.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<Testimonial>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("testimonials", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const createTestimonial = async () => {
    if (!draft.name.trim() || !draft.quote.trim()) {
      setError("A name and a quote are both required.");
      return;
    }
    setSaving(true);
    try {
      const created = await insertRow<Testimonial>("testimonials", { ...draft, name: draft.name.trim() });
      setRows((list) => [...list, created]);
      setCreateOpen(false);
      setDraft({ ...EMPTY_TESTIMONIAL });
      notify("Testimonial added.");
    } catch {
      setError("We couldn't create that testimonial. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    try {
      await deleteRow("testimonials", id);
      setRows((list) => list.filter((row) => row.id !== id));
      setSelectedId(null);
      notify("Testimonial deleted.");
    } catch {
      setError("We couldn't delete that testimonial. Please try again.");
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!query) return true;
      return [row.name, row.role || "", row.location || "", row.quote, row.experience || ""].join(" ").toLowerCase().includes(query);
    });
  }, [rows, search, statusFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;
  const avgRating = rows.length ? (rows.reduce((sum, row) => sum + Number(row.rating || 0), 0) / rows.length).toFixed(1) : "0.0";

  const columns: Column<Testimonial>[] = [
    {
      key: "name",
      header: "Visitor",
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.name}</p>
          <p className="text-xs text-foreground-500 mt-0.5">{row.role || "Visitor"}{row.location ? ` · ${row.location}` : ""}</p>
        </div>
      ),
    },
    { key: "quote", header: "Quote", render: (row) => <span className="text-sm text-foreground-700 line-clamp-1 max-w-xs">{row.quote}</span> },
    { key: "experience", header: "Experience", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.experience || "—"}</span> },
    { key: "rating", header: "Rating", render: (row) => <Stars rating={row.rating} /> },
    { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(TESTIMONIAL_STATUS, row.status)} /> },
    {
      key: "featured",
      header: "Featured",
      render: (row) =>
        row.featured ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent-900 bg-accent-100 border border-accent-300 px-2 py-1 rounded-full"><i className="ri-star-fill"></i> Yes</span>
        ) : (
          <span className="text-xs text-foreground-400">—</span>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Content"
        title="Testimonials"
        subtitle="The visitor quotes featured across the homepage — hand-picked social proof, separate from reviewed visitor reviews."
        actions={
          <button type="button" onClick={() => setCreateOpen(true)} className={btnPrimary}>
            <i className="ri-add-line"></i> New Testimonial
          </button>
        }
      />

      <StatCards
        stats={[
          { label: "Total", value: rows.length, icon: "ri-chat-quote-line", tone: "primary" },
          { label: "Published", value: rows.filter((row) => row.status === "published").length, icon: "ri-checkbox-circle-line", tone: "primary" },
          { label: "Featured", value: rows.filter((row) => row.featured).length, icon: "ri-star-line", tone: "accent" },
          { label: "Avg. rating", value: avgRating, icon: "ri-star-half-line", tone: "secondary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by name, quote, experience…" />
          <Select label="Status" value={statusFilter} onChange={setStatusFilter} options={[{ value: "all", label: "All statuses" }, ...optionsFrom(TESTIMONIAL_STATUS)]} />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} testimonials</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading testimonials…" />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-chat-quote-line" title={rows.length === 0 ? "No testimonials yet" : "No testimonials match"} message="Add a visitor quote to feature it on the site." />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.name || "Testimonial"}
        subtitle="Visitor quote"
        footer={
          selected ? (
            <button type="button" onClick={() => remove(selected.id)} className={btnGhost}>
              <i className="ri-delete-bin-line"></i> Delete
            </button>
          ) : null
        }
      >
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...metaFor(TESTIMONIAL_STATUS, selected.status)} />
              <Stars rating={selected.rating} />
            </div>

            <blockquote className="border-l-2 border-accent-400 pl-4 text-sm text-foreground-800 italic leading-relaxed">
              {selected.quote}
            </blockquote>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name">
                <input value={selected.name} onChange={(e) => update(selected.id, { name: e.target.value })} className={textInput} />
              </Field>
              <Field label="Role">
                <input value={selected.role || ""} onChange={(e) => update(selected.id, { role: e.target.value })} className={textInput} />
              </Field>
              <Field label="Location">
                <input value={selected.location || ""} onChange={(e) => update(selected.id, { location: e.target.value })} className={textInput} />
              </Field>
              <Field label="Experience">
                <input value={selected.experience || ""} onChange={(e) => update(selected.id, { experience: e.target.value })} className={textInput} />
              </Field>
            </div>

            <Field label="Quote">
              <textarea value={selected.quote} onChange={(e) => update(selected.id, { quote: e.target.value })} rows={4} maxLength={500} className={`${textInput} resize-none`}></textarea>
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Rating">
                <select value={selected.rating} onChange={(e) => update(selected.id, { rating: Number(e.target.value) })} className={fieldInput}>
                  {[5, 4, 3, 2, 1].map((value) => (<option key={value} value={value}>{value} stars</option>))}
                </select>
              </Field>
              <Field label="Status">
                <select value={selected.status} onChange={(e) => update(selected.id, { status: e.target.value }, "Status updated.")} className={fieldInput}>
                  {optionsFrom(TESTIMONIAL_STATUS).map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
                </select>
              </Field>
            </div>

            <button
              type="button"
              onClick={() => update(selected.id, { featured: !selected.featured }, selected.featured ? "Removed from featured." : "Marked as featured.")}
              className={`w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-3 rounded-md border transition-colors cursor-pointer ${
                selected.featured ? "bg-accent-500 text-accent-950 border-accent-500" : "bg-background-100 text-foreground-700 border-background-300"
              }`}
            >
              <i className={selected.featured ? "ri-star-fill" : "ri-star-line"}></i>
              {selected.featured ? "Featured on homepage" : "Not featured"}
            </button>
          </>
        ) : null}
      </Drawer>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add a testimonial"
        subtitle="New quote"
        footer={
          <>
            <button type="button" onClick={() => setCreateOpen(false)} className={btnGhost}>Cancel</button>
            <button type="button" onClick={createTestimonial} disabled={saving} className={btnPrimary}>
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
              Add testimonial
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Name">
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className={textInput} />
          </TextField>
          <TextField label="Role">
            <input value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} placeholder="e.g. Family of four" className={textInput} />
          </TextField>
          <TextField label="Location">
            <input value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} placeholder="e.g. Hyderabad" className={textInput} />
          </TextField>
          <TextField label="Experience">
            <input value={draft.experience} onChange={(e) => setDraft({ ...draft, experience: e.target.value })} placeholder="e.g. Farmer for a Day" className={textInput} />
          </TextField>
          <TextField label="Rating">
            <select value={draft.rating} onChange={(e) => setDraft({ ...draft, rating: Number(e.target.value) })} className={fieldInput}>
              {[5, 4, 3, 2, 1].map((value) => (<option key={value} value={value}>{value} stars</option>))}
            </select>
          </TextField>
          <TextField label="Status">
            <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} className={fieldInput}>
              {optionsFrom(TESTIMONIAL_STATUS).map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
            </select>
          </TextField>
          <div className="sm:col-span-2">
            <TextField label="Quote">
              <textarea value={draft.quote} onChange={(e) => setDraft({ ...draft, quote: e.target.value })} rows={3} maxLength={500} className={`${textInput} resize-none`}></textarea>
            </TextField>
          </div>
        </div>
      </Modal>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically.
      </p>
      {node}
    </div>
  );
}